/**
 * The listing's pager.
 *
 * A listing used to stop at 500 rows and say "capped — narrow the period or add
 * a filter to see the rest", which asked the reader to change the question in
 * order to be allowed to finish reading the answer. The rows past the cap were
 * frequently the ones the report was opened to find. This is what replaced it:
 * the cap became a PAGE SIZE, `offset` walks the rest, and `meta.totalRows`
 * makes "of 12,345" a fact rather than an estimate.
 *
 * It renders nothing when everything already fits on one page — a pager over a
 * 12-row report is noise, and callers should not have to gate on the count
 * themselves.
 *
 * Wording and shape follow the app's own `PaginationFooter`, deliberately: a
 * report is a list like any other, and this package cannot import that
 * component without taking the whole web app with it.
 */
import { DETAIL_PAGE_SIZES } from '@clockwise/semantic-core';
import { useEffect, useRef } from 'react';

interface Props {
  /** Rows skipped to reach this page. */
  offset: number;
  /** Rows per page — the spec's `limit`. */
  pageSize: number;
  /** Rows on THIS page, which is fewer than `pageSize` on the last one. */
  shown: number;
  /**
   * Rows in the whole answer. Absent when the transport cannot count them, in
   * which case the pager still steps forward and back but cannot say how far
   * it has to go.
   */
  total?: number;
  /** True while a page is in flight — both buttons stand down rather than queue. */
  busy?: boolean;
  /**
   * The server's own verdict that this page begins past the end of the answer
   * (`meta.pastEnd`), for the result currently on screen.
   *
   * Pass it and the recovery below acts on evidence instead of on the absence
   * of rows, which is the difference between correcting once and correcting on
   * every render that happens not to have data yet.
   */
  pastEnd?: boolean;
  onPage: (offset: number) => void;
  onPageSize: (size: number) => void;
}

export function Pager({
  offset,
  pageSize,
  shown,
  total,
  busy,
  pastEnd,
  onPage,
  onPageSize,
}: Props) {
  const page = Math.floor(offset / pageSize);
  // With no total there is no last page to count to, so "there may be more" is
  // the honest reading of a full page — and a short page is the end.
  //
  // Never fewer pages than the one being displayed: a stale or unknown total
  // paired with a far-in offset otherwise reads "Page 7 of 1", which is not a
  // sentence about anything. The recovery below is what actually resolves it;
  // this only keeps the readout from contradicting itself in the meantime.
  const pageCount =
    total === undefined ? undefined : Math.max(page + 1, Math.ceil(total / pageSize), 1);
  const hasNext = pageCount === undefined ? shown === pageSize : page + 1 < pageCount;

  // MID-FETCH there are no rows yet, which is not the same as no rows. Reading
  // the count off `shown` then would flash "No rows on this page" on every page
  // turn — the one message that should mean something. The page being fetched
  // is the page `offset` names, so its extent is the same arithmetic the Next
  // button just did, and it is right by the time the rows land.
  const inFlight = busy === true && shown === 0;
  const count = inFlight
    ? total === undefined
      ? pageSize
      : Math.max(0, Math.min(pageSize, total - offset))
    : shown;
  const start = count === 0 ? 0 : offset + 1;
  const end = offset + count;

  /**
   * A page past the end walks itself back.
   *
   * Reachable two ways, and neither is the reader's mistake: a saved spec whose
   * report has since shrunk, and — when the transport cannot count — a Next
   * offered on a full last page, since "this page is full" is the only evidence
   * of a further page there is. Without this the reader is stranded on an empty
   * page with a Next that does nothing and no total to explain why.
   *
   * It lives HERE rather than in the built-in builder so that a host drawing
   * its own UI around this component gets the recovery too — the headless path
   * is documented, and stranding is not something each host should rediscover.
   *
   * Back one page rather than to the first: the reader was reading somewhere.
   * A known total jumps straight to the last page; an unknown one steps, and
   * converges because each empty page steps again.
   */
  const step = useRef(onPage);
  step.current = onPage;

  /**
   * ONE correction per answer, not one per render.
   *
   * "No rows" is also what a render looks like in the gap between the page
   * changing and the fetch starting — the transport sets `loading` in an effect
   * after commit, so there is a commit with the new offset, no data and nothing
   * in flight. Correcting on that reading walks back another page, and another,
   * until a report the reader was reading the end of opens at page 1.
   *
   * So: `pastEnd` is the server saying this page really did run off the end,
   * and the latch covers a transport that cannot say it. The latch lifts when a
   * fetch starts, because whatever that fetch returns is an answer about THIS
   * offset — which is what makes the walk-back converge rather than stop dead.
   */
  const latch = useRef(false);
  if (busy || shown > 0) latch.current = false;
  const empty = pastEnd === true || (pastEnd === undefined && shown === 0);
  const stranded = empty && offset > 0 && !busy && !latch.current;
  useEffect(() => {
    if (!stranded) return;
    latch.current = true;
    const lastPage = total !== undefined && total > 0 ? Math.floor((total - 1) / pageSize) : null;
    const jump = lastPage !== null ? lastPage * pageSize : offset - pageSize;
    // STRICTLY back. `total` may be a value the host remembered from an earlier
    // query — a page past the end has no count of its own — and a stale one can
    // name the very page being escaped from. Dispatching that offset changes no
    // query, refetches nothing and leaves these deps untouched, so the effect
    // never fires again and the reader sits on an empty page. Stepping is the
    // floor: it always moves, and it converges because each empty page steps.
    step.current(Math.max(0, Math.min(jump, offset - pageSize)));
  }, [stranded, offset, pageSize, total]);

  // Nothing to page through: one page holds the answer, and it is the first.
  if (offset === 0 && total !== undefined && total <= pageSize) return null;

  return (
    <nav className="rk-pager" aria-label="Report pages">
      <span className="rk-pager-range">
        {count === 0
          ? 'No rows on this page'
          : `Showing ${start.toLocaleString('en-US')}–${end.toLocaleString('en-US')}${
              total === undefined ? '' : ` of ${total.toLocaleString('en-US')}`
            }`}
      </span>

      <label className="rk-pager-size">
        Rows per page
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          // Changing the size mid-flight would file the new page size against
          // the outgoing request's offset.
          disabled={busy}
        >
          {/* A saved report may carry a size this list does not offer — a
              deliberate one, or one from before the list changed. Showing it
              keeps the control honest about what is actually in force. */}
          {(DETAIL_PAGE_SIZES as readonly number[]).includes(pageSize) ? null : (
            <option value={pageSize}>{pageSize.toLocaleString('en-US')}</option>
          )}
          {DETAIL_PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size.toLocaleString('en-US')}
            </option>
          ))}
        </select>
      </label>

      <div className="rk-pager-steps">
        <button
          type="button"
          className="rk-button"
          disabled={page === 0 || busy}
          onClick={() => onPage(Math.max(0, offset - pageSize))}
        >
          Previous
        </button>
        <span className="rk-pager-page">
          Page {(page + 1).toLocaleString('en-US')}
          {pageCount === undefined ? '' : ` of ${pageCount.toLocaleString('en-US')}`}
        </span>
        <button
          type="button"
          className="rk-button"
          disabled={!hasNext || busy}
          onClick={() => onPage(offset + pageSize)}
        >
          Next
        </button>
      </div>
    </nav>
  );
}
